import type * as Immutable from 'immutable';

import type { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { getElementId, getImageProp } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { isSkeleton } from '../../../../../../../common/elements/utils/elementTypeUtils';
import { ICON_SEARCH_MODES } from '../../../../../../../common/icons/iconConstants';
import type { Icon } from '../../../../../../../common/icons/iconTypes';
import { sendBoardIconSetEvent } from '../../../../../../components/elementIcon/elementIconAnalyticsUtils';
import { searchForIcon } from '../../../../../../components/elementIcon/elementIconService';
import type { ElementIcon } from '../../../../../../components/elementIcon/elementIconTypes';
import { cleanElementAttachment } from '../../../../../../element/attachments/attachmentActions';
import { updateMultipleElements } from '../../../../../../element/actions/elementActions';
import globalLogger from '../../../../../../logger';
import type { Thunk } from '../../../../../../types/reduxTypes';
import type { IconPickerAction } from './iconPickerReducer';
import { selectIconSearches } from './iconPickerSelectors';
import {
    ICON_PICKER_RECENT_SEARCH_ADD,
    ICON_PICKER_SEARCH_ERROR,
    ICON_PICKER_SEARCH_LOADING,
    ICON_PICKER_SEARCH_SUCCESS,
    ICON_SEARCH_COUNT,
} from './iconPickerConstants';

const logger = globalLogger.createChannel('workspace.toolbar');

export const addRecentIconSearch = (elementId: string, query: string): IconPickerAction => ({
    type: ICON_PICKER_RECENT_SEARCH_ADD,
    elementId,
    query,
});

/**
 * Fetches icons for a query unless it's already loading or loaded.
 * Failed searches fetch again, so showing their tab or pressing retry tries once more.
 */
export const fetchIconSearch =
    (query: string): Thunk =>
    async (dispatch, getState) => {
        const existingSearch = selectIconSearches(getState())[query];
        if (existingSearch && existingSearch.status !== 'error') return;

        dispatch({ type: ICON_PICKER_SEARCH_LOADING, query });

        try {
            const response = await searchForIcon(query, ICON_SEARCH_COUNT);
            const icons: Icon[] = response.data.icons;

            dispatch({ type: ICON_PICKER_SEARCH_SUCCESS, query, icons });
        } catch (error) {
            logger.error('Failed to retrieve icons', error);
            dispatch({ type: ICON_PICKER_SEARCH_ERROR, query });
        }
    };

/**
 * Sets the icon on every selected board in one undo step.
 * Boards that had a custom image also lose the colours taken from it.
 */
export const setSelectedElementsIcon =
    ({
        icon,
        selectedElements,
        searchQuery,
    }: {
        icon: ElementIcon;
        selectedElements: Immutable.List<ImMNElement>;
        searchQuery: string;
    }): Thunk =>
    (dispatch) => {
        const firstElement = selectedElements.first();
        const loadedElements = selectedElements.filter((element) => !isSkeleton(element));

        sendBoardIconSetEvent({
            icon,
            boardId: getElementId(firstElement),
            searchQuery,
            isFallback: undefined,
            setMode: ICON_SEARCH_MODES.USER_ACTION,
        });

        const updates = loadedElements
            .map((element) => {
                const imageColourChanges = getImageProp(element)
                    ? { color: null, secondaryColor: null, autoColorIndex: null }
                    : {};

                return { id: getElementId(element), changes: { icon, image: null, ...imageColourChanges } };
            })
            .toArray();

        dispatch(updateMultipleElements({ updates }));

        // The action creator takes one `id`, so clean each board's attachment separately.
        loadedElements.forEach((element) => {
            const id = getElementId(element);
            dispatch(cleanElementAttachment({ id }));
        });
    };
